import { readTlv, children, TAG, oidToString } from '../bytes/der';
import { ParseError } from '../bytes/errors';
import { concat } from '../bytes/encoding';
import { HashAlgorithm, SignatureAlgorithm, type DigitallySigned } from '../tls/vectors';

export type KeyAlgorithm = 'ecdsa-p256' | 'ecdsa-p384' | 'rsa';

export interface PublicKey {
  key: CryptoKey;
  alg: KeyAlgorithm;
  spki: Uint8Array;
}

const OID_RSA = '1.2.840.113549.1.1.1';
const OID_EC = '1.2.840.10045.2.1';
const OID_P256 = '1.2.840.10045.3.1.7';
const OID_P384 = '1.3.132.0.34';

/** Reads the algorithm (and curve) out of a SubjectPublicKeyInfo. */
export function spkiAlgorithm(spki: Uint8Array): KeyAlgorithm {
  const outer = readTlv(spki, 0);
  if (outer.tag !== TAG.SEQUENCE) throw new ParseError('SPKI: expected SEQUENCE');
  const [algId] = children(outer);
  if (!algId || algId.tag !== TAG.SEQUENCE) throw new ParseError('SPKI: expected AlgorithmIdentifier');
  const [oid, params] = children(algId);
  if (!oid || oid.tag !== TAG.OID) throw new ParseError('SPKI: expected algorithm OID');
  const alg = oidToString(oid.content);
  if (alg === OID_RSA) return 'rsa';
  if (alg === OID_EC) {
    if (!params || params.tag !== TAG.OID) throw new ParseError('SPKI: EC key without named curve');
    const curve = oidToString(params.content);
    if (curve === OID_P256) return 'ecdsa-p256';
    if (curve === OID_P384) return 'ecdsa-p384';
    throw new ParseError(`SPKI: unsupported EC curve ${curve}`);
  }
  throw new ParseError(`SPKI: unsupported key algorithm ${alg}`);
}

export async function importSpki(spki: Uint8Array): Promise<PublicKey> {
  const alg = spkiAlgorithm(spki);
  const params: EcKeyImportParams | RsaHashedImportParams =
    alg === 'rsa'
      ? { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' }
      : { name: 'ECDSA', namedCurve: alg === 'ecdsa-p256' ? 'P-256' : 'P-384' };
  const key = await crypto.subtle.importKey('spki', spki as BufferSource, params, true, ['verify']);
  return { key, alg, spki };
}

/**
 * Converts a DER `ECDSA-Sig-Value ::= SEQUENCE { r INTEGER, s INTEGER }` into the fixed-width
 * `r || s` form (IEEE P1363) that WebCrypto expects. Handles leading 0x00 sign bytes and short values.
 */
export function ecdsaDerToP1363(der: Uint8Array, size = 32): Uint8Array {
  const seq = readTlv(der, 0);
  if (seq.tag !== TAG.SEQUENCE) throw new ParseError('ECDSA signature: expected SEQUENCE');
  if (seq.next !== der.length) throw new ParseError('ECDSA signature: trailing bytes');
  const ints = [...children(seq)];
  if (ints.length !== 2 || ints.some((t) => t.tag !== TAG.INTEGER)) {
    throw new ParseError('ECDSA signature: expected two INTEGERs');
  }
  const out = new Uint8Array(size * 2);
  ints.forEach((t, i) => {
    let v = t.content;
    if (v.length === 0) throw new ParseError('ECDSA signature: empty INTEGER');
    if (v[0]! & 0x80) throw new ParseError('ECDSA signature: negative INTEGER');
    while (v.length > 1 && v[0] === 0) v = v.subarray(1);
    if (v.length > size) throw new ParseError(`ECDSA signature: INTEGER longer than ${size} bytes`);
    out.set(v, i * size + (size - v.length));
  });
  return out;
}

/** Inverse of ecdsaDerToP1363, for display or re-encoding. */
export function ecdsaP1363ToDer(raw: Uint8Array): Uint8Array {
  const size = raw.length / 2;
  const int = (v: Uint8Array): Uint8Array => {
    let i = 0;
    while (i < v.length - 1 && v[i] === 0) i++;
    let body = v.subarray(i);
    if (body[0]! & 0x80) body = concat(new Uint8Array([0]), body);
    return concat(new Uint8Array([TAG.INTEGER, body.length]), body);
  };
  const content = concat(int(raw.subarray(0, size)), int(raw.subarray(size)));
  return concat(new Uint8Array([TAG.SEQUENCE, content.length]), content);
}

export interface VerifyResult {
  ok: boolean;
  /** Set when the signature could not even be checked (algorithm mismatch, malformed encoding). */
  error?: string;
}

/** Verifies a TLS `digitally-signed` struct over `input` with a CT log key. */
export async function verifyDigitallySigned(
  pk: PublicKey,
  ds: DigitallySigned,
  input: Uint8Array,
): Promise<VerifyResult> {
  try {
    if (pk.alg === 'rsa') {
      if (ds.signatureAlgorithm !== SignatureAlgorithm.rsa) return { ok: false, error: 'signature algorithm is not RSA' };
      if (ds.hashAlgorithm !== HashAlgorithm.sha256) return { ok: false, error: 'hash algorithm is not SHA-256' };
      const ok = await crypto.subtle.verify('RSASSA-PKCS1-v1_5', pk.key, ds.signature as BufferSource, input as BufferSource);
      return { ok };
    }
    if (ds.signatureAlgorithm !== SignatureAlgorithm.ecdsa) return { ok: false, error: 'signature algorithm is not ECDSA' };
    const p384 = pk.alg === 'ecdsa-p384';
    const wantHash = p384 ? HashAlgorithm.sha384 : HashAlgorithm.sha256;
    if (ds.hashAlgorithm !== wantHash) return { ok: false, error: `hash algorithm is not ${p384 ? 'SHA-384' : 'SHA-256'}` };
    const sig = ecdsaDerToP1363(ds.signature, p384 ? 48 : 32);
    const ok = await crypto.subtle.verify(
      { name: 'ECDSA', hash: p384 ? 'SHA-384' : 'SHA-256' },
      pk.key,
      sig as BufferSource,
      input as BufferSource,
    );
    return { ok };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}
